import { personOriginFromSearch, personRecordHref } from "@/lib/registrar-origin";
/**
 * Courses (issue #11): the registrar's Courses list at `/admin/courses`, and
 * the course workspace each seat opens at `<shell>/courses/:courseId`. A seat
 * has no course list here: its Home's course collection lists every course, so
 * a seat path without a course lands on that Home.
 *
 * The registrar list is a register only. Each row opens the course record at
 * `/admin/courses/{courseId}` (`RegistrarCoursePage`), which owns editing,
 * publication, archive, discard, the roster, and the record's other views.
 * `?create=section` opens a Task beside the register. Its receipt stays until
 * the registrar chooses the new section record.
 *
 * RLS scopes rows: a student to their enrollments, and, since migration 066, an
 * instructor to the sections they are assigned. The seat path still narrows the
 * instructor list itself, deliberately, as defence in depth over a boundary that
 * now holds without it. Role-gating here is UX only:
 *   - instructor: on courses where the API derives `can_teach`, edit HOW THE
 *     SECTION RUNS (delivery, schedule, meeting destination, location) plus
 *     materials and syllabus. The registrar half of the record (code, title,
 *     credits, program, term, capacity, instructor of record, and publication)
 *     renders as facts, because `courses.py` 403s each of those fields by name
 *     for a non-admin caller.
 *   - student: read-only.
 */
import { useLayoutEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, Navigate, useLocation, useSearchParams } from "react-router";

import { CourseWorkspace } from "@/components/courses/CourseWorkspace";
import { ArchivedCourseRecord } from "@/components/courses/ArchivedCourseRecord";
import { RegistrarCourseCreate } from "@/components/registrar/course/RegistrarCourseCreate";
import { RegistrarLocalNavigation } from "@/components/registrar/RegistrarLocalNavigation";
import { SectionsRegister } from "@/components/courses/SectionsRegister";
import { SECTIONS_COPY, facetRead, sectionsList, useSectionsRead } from "@/components/courses/sections-list";
import { useAuth } from "@/contexts/AuthContext";
import { useTenantConfig } from "@/hooks/use-tenant-config";
import { academicStructureApi } from "@/lib/api/academicStructure";
import { coursesApi } from "@/lib/api/courses";
import { courseHomeApi } from "@/lib/api/courseHome";
import { NONE } from "@/lib/course-form";
import {
  PROTECTED_ROUTES,
  registrarCoursePath,
  registrarRecordDestination,
  type CourseWorkspaceShell,
} from "@/navigation/routes";

interface CoursesPageProps {
  workspace?: "role" | "teaching" | "learning";
  courseId?: string;
  shell?: CourseWorkspaceShell;
}

export function CoursesPage(props: CoursesPageProps) {
  const { role } = useAuth();
  const teaching = props.workspace === "teaching"
    || (props.workspace !== "learning" && role === "instructor");
  const learning = !teaching && (props.workspace === "learning" || role === "student");
  if (teaching || learning) {
    const courseId = props.courseId;
    const shell: CourseWorkspaceShell = props.shell
      ?? (learning ? "student" : props.workspace === "teaching" ? "adminTeaching" : "instructor");
    return courseId
      ? <SeatCoursePage workspace={props.workspace ?? "role"} courseId={courseId} shell={props.shell} />
      : <Navigate to={PROTECTED_ROUTES[shell].dashboard} replace />;
  }
  return <RegistrarCoursesList />;
}

/** A path plus the given query, appended after any query the path already has. */
function withQuery(path: string, params: URLSearchParams): string {
  const query = params.toString();
  return query ? `${path}${path.includes("?") ? "&" : "?"}${query}` : path;
}

function RegistrarCoursesList() {
  const root = useRef<HTMLDivElement>(null);
  const { pathname, search, hash } = useLocation();
  const [q, setQ] = useState("");
  const [programId, setProgramId] = useState(NONE);
  const [semesterId, setSemesterId] = useState(NONE);
  const [searchParams, setSearchParams] = useSearchParams();
  // `?course=` is a retired address of the course record; routes.ts owns where
  // it lands.
  const retiredArrival = registrarRecordDestination(pathname, search, hash);
  const createMode = searchParams.get("create") === "section";
  const handoffStudentId = searchParams.get("student");
  const status = searchParams.get("status") ?? NONE;
  const archivedView = status === "archived";
  const setStatus = (value: string) => setSearchParams((previous) => {
    const next = new URLSearchParams(previous);
    if (value === NONE) next.delete("status");
    else next.set("status", value);
    return next;
  });

  // Arrival state a course record or a new draft carries forward: an
  // Admissions handoff (`student`, `origin`, `applicant`) and anything a later
  // contract adds. The list's own `create` and `status` stay on the list.
  const carried = new URLSearchParams(searchParams);
  carried.delete("create");
  carried.delete("status");
  const createSearch = new URLSearchParams(searchParams);
  createSearch.delete("create");
  createSearch.set("create", "section");
  const createHref = `${PROTECTED_ROUTES.admin.courses}?${createSearch}`;
  const wasCreating = useRef(createMode);
  useLayoutEffect(() => {
    if (wasCreating.current && !createMode) {
      const opener = [...(root.current?.querySelectorAll<HTMLAnchorElement>('a[data-nav]') ?? [])].find(link => link.getAttribute("href") === createHref);
      opener?.focus();
    }
    wasCreating.current = createMode;
  }, [createMode, createHref]);
  const listSearch = new URLSearchParams(searchParams);
  listSearch.delete("create");
  const cancelHref = withQuery(PROTECTED_ROUTES.admin.courses, listSearch);
  const listing = retiredArrival === null;
  // One read of every section: the rows, the figures and the filter's hidden
  // count all come from it (R5); the List's tools narrow it in place (R6).
  const read = useSectionsRead(listing);
  const programs = useQuery({ queryKey: ["programs"], queryFn: academicStructureApi.getPrograms, enabled: listing });
  const semesters = useQuery({ queryKey: ["semesters"], queryFn: academicStructureApi.getSemesters, enabled: listing });

  if (retiredArrival !== null) return <Navigate replace to={retiredArrival} />;

  const term = (semesters.data ?? []).find((semester) => semester.id === semesterId);
  const list = sectionsList({
    read,
    filters: {
      query: q,
      programId,
      term: term ? { id: term.id, name: term.name, closed: term.status === "closed" } : null,
      status,
    },
    archived: archivedView,
    // With an Admissions handoff the registrar is choosing where to enrol one
    // student, so a row opens that course's roster with the student carried.
    rowAct: (course) => handoffStudentId
      ? { does: "exit", verb: SECTIONS_COPY.openRoster, to: withQuery(registrarCoursePath(course.id, "roster"), carried) }
      : { does: "visit", verb: SECTIONS_COPY.open, visit: `course:${course.id}/details` },
    historyHref: (termId) => `${PROTECTED_ROUTES.admin.courseHistory}?term_id=${encodeURIComponent(termId)}`,
    handoff: Boolean(handoffStudentId),
    onQuery: setQ,
    // This address reveals the naming Task before the retained register.
    exits: [
      { label: SECTIONS_COPY.add, to: createHref },
      { label: SECTIONS_COPY.intake, to: PROTECTED_ROUTES.admin.classroomIntake },
    ],
    facets: [
      {
        label: "Status",
        value: status,
        onChange: setStatus,
        options: [
          { value: NONE, label: "Active sections" },
          { value: "draft", label: "Active, draft" },
          { value: "published", label: "Active, published" },
          { value: "archived", label: "Archive" },
        ],
      },
      {
        label: "Program",
        value: programId,
        onChange: setProgramId,
        options: [{ value: NONE, label: "All programs" }, ...(programs.data ?? []).map((program) => ({ value: program.id, label: program.name }))],
        read: facetRead(programs, SECTIONS_COPY.programsFailed),
      },
      {
        label: "Term",
        value: semesterId,
        onChange: setSemesterId,
        options: [
          { value: NONE, label: "All terms" },
          ...(semesters.data ?? []).map((semester) => ({ value: semester.id, label: `${semester.name}${semester.status === "closed" ? " · closed" : ""}` })),
        ],
        read: facetRead(semesters, SECTIONS_COPY.termsFailed),
      },
    ],
  });

  return (
    <div ref={root} className="app-page app-page--roomy">
      <RegistrarLocalNavigation area="courses" />
      {personOriginFromSearch(searchParams) && <Link to={personRecordHref(personOriginFromSearch(searchParams)!)}>Return to student record</Link>}

      {createMode ? <RegistrarCourseCreate list={list} cancelHref={cancelHref} recordLabel={handoffStudentId ? "Build roster" : undefined} recordHref={id => withQuery(registrarCoursePath(id, handoffStudentId ? "roster" : "record"), carried)} /> : <SectionsRegister list={list} />}
    </div>
  );
}

/**
 * One course workspace in a seat shell. The catalogue row, when this reader can
 * see one, decides whether they may edit; with no row, the seat is the proof
 * of the relationship, because the teaching projection is scoped server-side
 * to active assignments. A learner never edits.
 */
export function SeatCoursePage({
  workspace,
  courseId,
  shell: routeShell,
}: {
  workspace: "role" | "teaching" | "learning";
  courseId: string;
  shell?: CourseWorkspaceShell;
}) {
  const { role: accountRole, me } = useAuth();
  const role = workspace === "teaching" ? "instructor" : workspace === "learning" ? "student" : accountRole;
  const learning = role === "student";
  const { sessionPortalEnabled } = useTenantConfig();
  const [searchParams] = useSearchParams();
  const courseShell: CourseWorkspaceShell = routeShell
    ?? (workspace === "teaching" ? "adminTeaching" : learning ? "student" : "instructor");

  const filters = {
    status: searchParams.get("status") ?? undefined,
    learning: workspace === "learning" || undefined,
  };
  const courses = useQuery({ queryKey: ["courses", filters], queryFn: () => coursesApi.list(filters) });
  const selectedSummary = (courses.isError ? [] : courses.data ?? []).find((course) =>
    course.id === courseId
    && course.status !== "archived"
    && (learning || course.can_teach === true));
  const selectedRecord = useQuery({
    queryKey: ["archived-course-record", me?.tenant_id, me?.user_id, courseId],
    queryFn: () => coursesApi.get(courseId),
    enabled: !!me && !courses.isPending && !selectedSummary,
    retry: false,
  });
  const learningRecord = useQuery({
    queryKey: ["response-course", `${me?.tenant_id}:${me?.user_id}`, courseId],
    queryFn: () => courseHomeApi.getLearningContext(courseId),
    enabled: Boolean(me && learning), retry: false,
  });
  const enrollmentStatus = !learningRecord.isError ? learningRecord.data?.relationship.enrollment_status : undefined;
  const record = selectedSummary ?? selectedRecord.data;
  if (learning && record && !selectedRecord.isError && (enrollmentStatus === "incomplete" || enrollmentStatus === "complete")) {
    return <ArchivedCourseRecord course={record} enrollmentStatus={enrollmentStatus} workspaceHref={PROTECTED_ROUTES[courseShell].dashboard} />;
  }
  const canEditSelected = selectedSummary !== undefined
    ? role === "instructor" && selectedSummary.can_teach === true
    : role === "instructor";
  // The old ?edit=details address opens the registered details visit in
  // place, so Manage course draws one details editor, never a second (#1416).
  if (searchParams.get("edit") === "details") {
    const next = new URLSearchParams(searchParams);
    next.delete("edit");
    if (canEditSelected) next.set("visit", `course:${courseId}/details`);
    return <Navigate replace to={{ search: `?${next}` }} />;
  }

  if (!selectedSummary && selectedRecord.isFetchedAfterMount && !selectedRecord.isError && !selectedRecord.isFetching && selectedRecord.data?.id === courseId && selectedRecord.data.status === "archived") {
    return <ArchivedCourseRecord course={selectedRecord.data} teaching={!learning && selectedRecord.data.can_teach === true} workspaceHref={PROTECTED_ROUTES[courseShell].dashboard} />;
  }

  return (
    <div className="min-w-0 w-full">
      <section className="grid min-w-0 gap-4" aria-label="Selected course">
        <CourseWorkspace
          courseId={courseId}
          perspective={learning ? "learning" : "teaching"}
          canEdit={canEditSelected}
          portalShell={workspace === "teaching" ? "adminTeaching" : learning ? "student" : "instructor"}
          portalEnabled={sessionPortalEnabled}
          workspaceHref={PROTECTED_ROUTES[courseShell].dashboard}
        />
      </section>
    </div>
  );
}
